import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import ListItem from '@mui/joy/ListItem';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import CircularProgress from '@mui/joy/CircularProgress';
import useLazyAgents from '../../hooks/useLazyAgents';
import useListboxPaging from '../../hooks/useListboxPaging';

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Single-select agent picker that fetches nothing until its listbox is opened,
 * then pulls the first 20 agents and appends 10 more per "Load more" — holding
 * only `{ id, name }` per row. Built for accounts with very large agent counts,
 * replacing dropdowns that used to enumerate every agent up front.
 *
 * The currently selected agent is pinned at the top and its label is retained
 * even while the list is unfetched (via `renderValue`), so the control always
 * shows the active selection.
 *
 * @param {Object} props
 * @param {string|null} props.value                Selected agent id.
 * @param {(id:string)=>void} props.onChange        Fired with the picked agent id.
 * @param {{id:string,name?:string}} [props.currentAgent] Pinned at top; keeps its label when closed.
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.searchable=false]        Show a debounced name search in the listbox header.
 * @param {Object} [props.sx]
 */
export default function LazyAgentSelect({
  value,
  onChange,
  currentAgent,
  placeholder = 'Choose agent…',
  disabled = false,
  searchable = false,
  sx,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Reset the search box whenever the listbox closes so a re-open starts clean.
  useEffect(() => {
    if (!open) {
      setSearch('');
      setSearchDebounced('');
    }
  }, [open]);

  // Focus the search field on open, deferred so it wins the race against
  // useSelect's own "focus the highlighted option" effect (a native autoFocus
  // fires too early and gets bounced off).
  useEffect(() => {
    if (!searchable || !open) return undefined;
    const raf = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [searchable, open]);

  const { agents, loading, loadingMore, hasLoaded, hasMore, error, loadMore } = useLazyAgents({
    enabled: open,
    search: searchDebounced || undefined,
    searchField: 'name',
  });

  // Treat the pre-fetch frame (open, effect hasn't run yet) as loading so the
  // list doesn't flash an "empty" state before the first page arrives.
  const showSpinner = loading || (open && !hasLoaded);

  // Accumulate id -> name for every agent we've seen (pinned current + fetched),
  // so the trigger can still label the selection after the listbox closes.
  const labelByIdRef = useRef({});
  useMemo(() => {
    if (currentAgent?.id) labelByIdRef.current[currentAgent.id] = currentAgent.name || currentAgent.id;
    for (const a of agents) if (a?.id) labelByIdRef.current[a.id] = a.name || a.id;
  }, [currentAgent, agents]);

  // Merge the pinned current agent in front of the fetched rows, deduped by id,
  // and always keep the selected `value` mounted as an Option — Joy skips
  // renderValue for an unmounted option, so a value that isn't on the current
  // page would otherwise show as the placeholder once the listbox closes.
  const rows = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (id, name) => {
      if (id == null || id === '' || seen.has(id)) return;
      out.push({ id, name: name || labelByIdRef.current[id] || id });
      seen.add(id);
    };
    if (currentAgent?.id) push(currentAgent.id, currentAgent.name);
    for (const a of agents) push(a?.id, a?.name);
    push(value, labelByIdRef.current[value]);
    return out;
  }, [currentAgent, agents, value]);

  // Auto-load more when scrolled near the bottom, and keep the scroll position
  // across appends (so the list doesn't snap back to the pinned top row).
  const { listboxRef, onScroll } = useListboxPaging({
    hasMore,
    busy: loading || loadingMore,
    loadMore,
    itemCount: rows.length,
  });

  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue == null) return;
      onChange?.(newValue);
      setOpen(false);
    },
    [onChange],
  );

  const renderValue = useCallback(
    (option) => {
      // When the listbox is closed the Options unmount, so Joy can't resolve the
      // selected option and passes null — fall back to the controlled `value`.
      const id = option?.value ?? value;
      if (id == null || id === '') {
        return <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>{placeholder}</Typography>;
      }
      return labelByIdRef.current[id] || option?.label || id;
    },
    [placeholder, value],
  );

  // Stop the listbox's roving-focus / typeahead handlers from stealing events
  // aimed at the search field and the "Load more" button, so neither selects an
  // option nor closes the popup.
  const swallow = {
    onMouseDown: (e) => e.stopPropagation(),
    onKeyDown: (e) => e.stopPropagation(),
    onClick: (e) => e.stopPropagation(),
  };

  return (
    <Select
      value={value ?? null}
      onChange={handleChange}
      listboxOpen={open}
      onListboxOpenChange={setOpen}
      placeholder={placeholder}
      disabled={disabled}
      renderValue={renderValue}
      sx={sx}
      slotProps={{ listbox: { ref: listboxRef, onScroll, sx: { maxHeight: 320, overflow: 'auto' } } }}
    >
      {searchable && (
        <ListItem sticky {...swallow} sx={{ p: 0.5 }}>
          <Input
            size="sm"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{ input: { ref: searchInputRef } }}
            {...swallow}
            sx={{ width: '100%' }}
          />
        </ListItem>
      )}

      {rows.map((a) => (
        <Option key={a.id} value={a.id}>
          {a.name || a.id}
        </Option>
      ))}

      {showSpinner && (
        <ListItem sx={{ justifyContent: 'center', py: 1 }}>
          <CircularProgress size="sm" />
        </ListItem>
      )}

      {error && !loading && (
        <ListItem>
          <Typography level="body-xs" color="danger">{error}</Typography>
        </ListItem>
      )}

      {hasLoaded && !loading && !error && rows.length === 0 && (
        <ListItem>
          <Typography level="body-xs" color="neutral">
            {searchDebounced ? 'No matching agents.' : 'No agents.'}
          </Typography>
        </ListItem>
      )}

      {hasMore && (
        <ListItem {...swallow} sx={{ justifyContent: 'center', py: 0.5 }}>
          <Button
            size="sm"
            variant="plain"
            loading={loadingMore}
            {...swallow}
            onMouseDown={(e) => {
              // Keep focus on the listbox so it doesn't blur-close or scroll-reset.
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              loadMore();
            }}
          >
            Load more
          </Button>
        </ListItem>
      )}
    </Select>
  );
}
